import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { booksConfig, createBooksClient } from '../books/booksClient';
import { projectService } from '../services/projectService';
import { Checkbox, Heading, Text } from './ui';

/**
 * Who the office sends to this property: the organization's FIELD members,
 * each a checkbox writing `projects.assignedTo`. A field member's project
 * list is exactly their assignments (`seesOnlyAssignedProjects` in
 * @shared-lib/org-access), so this is how a tech gets a house to scan —
 * field staff do not create projects themselves.
 *
 * Members live in the books' Postgres, not Firestore, so the list is read
 * from `organization_members` with the caller's own token (a member may read
 * their organization's rows). Shown only to someone who may edit the project,
 * and only for a project an organization owns.
 */

/** `uids` with `uid` added or removed. Pure; keeps the order it was given. */
export function toggleAssignee(uids, uid) {
  const current = Array.isArray(uids) ? uids : [];
  return current.includes(uid)
    ? current.filter((u) => u !== uid)
    : [...current, uid];
}

/** The organization's field members, `{ uid, email }`, by email. */
async function loadFieldMembers(orgId) {
  const client = createBooksClient({
    getToken: () => auth.currentUser.getIdToken(),
  });
  const rows = await client.select('organization_members', {
    select: 'firebase_uid,email,role',
    organization_id: `eq.${orgId}`,
    role: 'eq.field',
  });
  return rows
    .map((r) => ({ uid: r.firebase_uid, email: r.email }))
    .sort((a, b) => (a.email || '').localeCompare(b.email || ''));
}

export default function ProjectAssignments({ project, projectId }) {
  const [members, setMembers] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [assigned, setAssigned] = useState(project.assignedTo || []);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => setAssigned(project.assignedTo || []), [project.assignedTo]);

  useEffect(() => {
    let cancelled = false;
    if (!booksConfig()) {
      setLoadError('The organization directory is not configured here.');
      return undefined;
    }
    loadFieldMembers(project.orgId)
      .then((rows) => !cancelled && setMembers(rows))
      .catch((error) => {
        console.error('Could not load the field staff:', error);
        if (!cancelled) setLoadError('Could not load your field staff.');
      });
    return () => {
      cancelled = true;
    };
  }, [project.orgId]);

  const toggle = async (uid) => {
    const next = toggleAssignee(assigned, uid);
    const previous = assigned;
    setAssigned(next);
    setSaving(true);
    setSaveError(null);
    try {
      await projectService.updateProject(projectId, { assignedTo: next });
    } catch (error) {
      console.error('Could not save the assignment:', error);
      setAssigned(previous);
      setSaveError('Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="content-section" data-testid="project-assignments">
      <Heading level={3} tone="dark">
        Field staff
      </Heading>
      <Text tone="dark" variant="muted">
        The people you assign see this property in their list and can scan it,
        add photos and write notes.
      </Text>
      {loadError && (
        <Text tone="dark" variant="muted">
          {loadError}
        </Text>
      )}
      {!loadError && members === null && (
        <Text tone="dark" variant="muted">
          Loading…
        </Text>
      )}
      {members?.length === 0 && (
        <Text tone="dark" variant="muted">
          No one in your organization has the field role yet.
        </Text>
      )}
      {members?.map((m) => (
        <Checkbox
          key={m.uid}
          tone="dark"
          checked={assigned.includes(m.uid)}
          disabled={saving}
          onChange={() => toggle(m.uid)}
        >
          {m.email}
        </Checkbox>
      ))}
      {saveError && (
        <Text tone="dark" variant="muted" role="alert">
          {saveError}
        </Text>
      )}
    </section>
  );
}
